import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Space } from '@components/atoms/Space';
import { Form } from '@components/atoms/Form';
import { Input, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Popup } from '@components/atoms/Popup';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { TranslationTabs } from '@components/molecules/TranslationTabs';
import { useData } from '@hooks/useData';
import { useNotifications } from '@hooks/useNotifications';
import { useGetMyAccountStoreDetail } from '@services/useGetMyAccountStoreDetail';
import { useUpdateStore } from '@services/useUpdateStore';
import { InputErrors, Translations } from '@types';
import { convertFilesToBase64 } from '@utils/image';
import { CompanyEditBlock } from '../../MyCompany';
import styles from '../../MyCompany.module.css';
import { updateTranslation } from '@utils/translation';
import { useGetCaes } from '@services/useGetCaes';
import { useGetZones } from '@services/useGetZones';
import MultipleSelectWithSearch from '@components/atoms/MultipleSelectWithSearch/MultipleSelectWithSearch';
import { ErrorMessages } from '@components/atoms/ErrorMessages';
import { Select, SelectType } from '@components/atoms/Select/Select';
import { Modules, useModule } from '@hooks/useModule';

interface EditCompanyFormErrors {
    name: InputErrors;
    caes: InputErrors;
    zone: InputErrors;
    website: InputErrors;
    banner: InputErrors;
    logo: InputErrors;
}

interface EditCompanyModalProps {
    type: string;
    block: CompanyEditBlock | null;
    onClose: () => void;
}

export const EditCompanyModal: FC<EditCompanyModalProps> = ({ type, block, onClose }) => {
    const { showDefaultNotification } = useNotifications();
    const { request: requestAllCaes } = useGetCaes();
    const { request: requestAllZones } = useGetZones();
    const {
        request: requestUpdateStore,
        errorMessage,
        validationErrors,
        success: successMessage,
        lastResponseTime,
    } = useUpdateStore();
    const { request: requestMyAccountStoreDetail } = useGetMyAccountStoreDetail();
    const {
        myAccountStoreDetail,
        isLoadingMyAccountStoreDetail,
        allLanguages,
        isLoadingAllCaes,
        allCaes,
        isLoadingAllZones,
        allZones,
        allSettings,
    } = useData();
    const { t } = useTranslation();

    const automaticTranslations = allSettings?.data?.module_automatic_translations?.active;
    const languagesToTranslate = allLanguages?.data?.filter((item) => item.default !== 1) || [];

    const translations = {
        title: t('edit-company-modal-component.title'),
        nameLabel: t('edit-company-modal-component.name.label'),
        caeLabel: t('edit-company-modal-component.cae.label'),
        zoneLabel: t('edit-company-modal-component.zone.label'),
        zoneRequiredError: t('edit-company-modal-component.zone.required-error'),
        websiteLabel: t('edit-company-modal-component.website.label'),
        nameRequiredError: t('edit-company-modal-component.name.required-error'),
        caeRequiredError: t('edit-company-modal-component.cae.required-error'),
        bannerLabel: t('edit-company-modal-component.banner.label'),
        bannerRequiredError: t('edit-company-modal-component.banner.required-error'),
        bannerAlt: t('edit-company-modal-component.banner.alt'),
        logoLabel: t('edit-company-modal-component.logo.label'),
        logoRequiredError: t('edit-company-modal-component.logo.required-error'),
        logoAlt: t('edit-company-modal-component.logo.alt'),
        aboutTextLabel: t('edit-company-modal-component.about-text.label'),
        saveButton: t('edit-company-modal-component.save.button'),
    };

    const formRef = useRef<HTMLFormElement>(null);
    const [name, setName] = useState<string>('');
    const [caes, setCaes] = useState<{ uuid?: string; cae?: string; description?: string }[] | []>([]);
    const [zone, setZone] = useState<string>('');
    const [website, setWebsite] = useState<string>('');
    const [banner, setBanner] = useState<string | null>(null);
    const [logo, setLogo] = useState<string | null>(null);
    const [bannerPreview, setBannerPreview] = useState<string | null>(null);
    const [newBannerPreview, setNewBannerPreview] = useState<string | ArrayBuffer | null>(null);
    const [newLogoPreview, setNewLogoPreview] = useState<string | ArrayBuffer | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [socialLinks, setSocialLinks] = useState<{ name: string; url: string }[]>([]);
    const [aboutText, setAboutText] = useState<string>('');
    const [aboutTextTranslations, setAboutTextTranslations] = useState<Translations>([]);

    // force the image inputs to reset
    const [bannerInputKey, setBannerInputKey] = useState(Date.now());
    const [logoInputKey, setLogoInputKey] = useState(Date.now());

    useEffect(() => {
        !isLoadingAllCaes && requestAllCaes();
        !isLoadingAllZones && requestAllZones();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // when modal opens, update values for modal
    useEffect(() => {
        if (block !== null) {
            setName(myAccountStoreDetail?.data?.commercial_name || '');
            setCaes(myAccountStoreDetail?.data?.caes ?? []);
            setZone(myAccountStoreDetail?.data?.zone_uuid || '');
            setWebsite(myAccountStoreDetail?.data?.website || '');
            setBannerPreview(myAccountStoreDetail?.data?.banner_image?.src || null);
            setLogoPreview(myAccountStoreDetail?.data?.logo_img?.src || null);
            setSocialLinks(
                (myAccountStoreDetail?.data?.social_networks || []).map((item) => {
                    return { name: item.name || '', url: item.url || '' };
                })
            );
            setAboutText(myAccountStoreDetail?.data?.about_text || '');
            setAboutTextTranslations(
                myAccountStoreDetail?.data?.translations
                    ?.filter((translation) => translation.name === 'about_text')
                    .map((translation) => {
                        return { languageCode: translation.language_code || '', translation: translation.text || '' };
                    }) || []
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [block]);

    // errors to be displayed in the form
    const [errors, setErrors] = useState<EditCompanyFormErrors>({
        name: [],
        caes: [],
        zone: [],
        website: [],
        banner: [],
        logo: [],
    });

    const handleOnClickSave = () => {
        const errors = validateForm();

        if (
            errors.name.length === 0 &&
            errors.caes.length === 0 &&
            errors.website.length === 0 &&
            errors.banner.length === 0 &&
            errors.logo.length === 0
        ) {
            formRef?.current?.requestSubmit();
        }
    };

    const validateForm: () => EditCompanyFormErrors = () => {
        const newErrors: EditCompanyFormErrors = {
            name: [],
            caes: [],
            zone: [],
            website: [],
            banner: [],
            logo: [],
        };

        block === CompanyEditBlock.banner && !banner && newErrors.banner.push(translations.bannerRequiredError);
        block === CompanyEditBlock.logo && !logo && newErrors.logo.push(translations.logoRequiredError);

        if (block === CompanyEditBlock.nameAndSocialLinks) {
            !name.trim() && newErrors.name.push(translations.nameRequiredError);
            !caes && newErrors.caes.push(translations.caeRequiredError);
        }

        setErrors(newErrors);

        return newErrors;
    };

    const submitForm: () => void = async () => {
        const bannerInput = document.getElementById('banner-input') as HTMLInputElement;
        const logoInput = document.getElementById('logo-input') as HTMLInputElement;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const base64BannerImages: any = bannerInput?.files ? await convertFilesToBase64(bannerInput.files) : null;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const base64LogoImages: any = logoInput?.files ? await convertFilesToBase64(logoInput.files) : null;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const inputTranslations: any = {};

        aboutTextTranslations.forEach((item) => (inputTranslations[`about_text_${item.languageCode}`] = item.translation));

        let infoToUpdate = {};
        if (block === CompanyEditBlock.banner) {
            infoToUpdate = { banner_image: base64BannerImages?.[0] || undefined };
        } else if (block === CompanyEditBlock.logo) {
            infoToUpdate = { logo_img: base64LogoImages?.[0] || undefined };
        } else if (block === CompanyEditBlock.nameAndSocialLinks) {
            infoToUpdate = { commercial_name: name, caes: caes, url: website, zone_uuid: zone, social_networks: socialLinks };
        } else if (block === CompanyEditBlock.aboutText) {
            infoToUpdate = { about_text: aboutText, ...inputTranslations };
        }

        requestUpdateStore({ uuid: myAccountStoreDetail?.data?.uuid || '' }, infoToUpdate);
    };

    // update preview of new banner
    useEffect(() => {
        const bannerInput = document.getElementById('banner-input') as HTMLInputElement;
        const file = bannerInput?.files?.[0];

        if (file) {
            const reader = new FileReader();
            reader.onload = () => setNewBannerPreview(reader.result); // a base64 string of the image
            reader.readAsDataURL(file);
        } else {
            setNewBannerPreview(null);
        }
    }, [banner, bannerInputKey, block]);

    // update preview of new logo
    useEffect(() => {
        const logoInput = document.getElementById('logo-input') as HTMLInputElement;
        const file = logoInput?.files?.[0];

        if (file) {
            const reader = new FileReader();
            reader.onload = () => setNewLogoPreview(reader.result); // a base64 string of the image
            reader.readAsDataURL(file);
        } else {
            setNewLogoPreview(null);
        }
    }, [logo, logoInputKey, block]);

    // if edit service returned successfully, dispatch notification and update
    // if not, show error
    useEffect(() => {
        if (lastResponseTime) {
            if (errorMessage) {
                showDefaultNotification({ type: 'error', message: errorMessage || '' });
            } else if (validationErrors) {
                setErrors({
                    // match form request errors with frontend errors object
                    name: validationErrors?.name || [],
                    caes: validationErrors?.caes || [],
                    zone: validationErrors?.zone || [],
                    website: validationErrors?.website || [],
                    banner: validationErrors?.banner_image || [],
                    logo: validationErrors?.logo_img || [],
                });
            } else {
                showDefaultNotification({ type: 'success', message: successMessage || '' });
                // update form fields
                onClose();
                requestMyAccountStoreDetail({ uuid: myAccountStoreDetail?.data?.uuid || '' });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    const handleCAEsChange = (selectedUuids: string[]) => {
        const updatedCaes = allCaes?.data.filter((cae) => selectedUuids.includes(cae.uuid ?? ''));
        setCaes(updatedCaes ?? []);
    };

    const { isModuleActive } = useModule();

    return (
        <Popup
            show={block !== null}
            onHide={() => onClose()}
            desktopWidth="1220px"
            fullWidthOnMobile={true}
            header={
                <>
                    <div className={styles.header}>
                        <Typography tagVariant={TypographyTagVariants.h5} fontWeight="900" fontSize="20">
                            {translations.title}
                        </Typography>
                    </div>
                    <hr className={styles.divider} />
                </>
            }>
            <div className={styles['content-container']}>
                <Form formRef={formRef} onSubmit={submitForm}>
                    <div className="d-flex flex-column gap-3">
                        {/* banner */}
                        {block === CompanyEditBlock.banner && (
                            <div className="col-12">
                                <div className="d-flex align-items-center">
                                    {!isLoadingMyAccountStoreDetail && (
                                        <div className="position-relative">
                                            {newBannerPreview || bannerPreview ? (
                                                <img
                                                    src={(newBannerPreview as string) || bannerPreview || ''}
                                                    className={styles['image-preview']}
                                                    alt={translations.bannerAlt}
                                                />
                                            ) : (
                                                <div className={styles['image-preview']}></div>
                                            )}
                                            <div className="position-absolute top-0 end-0 p-1">
                                                {newBannerPreview && (
                                                    <button type="button" onClick={() => setBannerInputKey(Date.now())}>
                                                        <i className="fa-solid fa-xmark" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    <Space orientation="horizontal" spaceBreakpoints={{ xs: { space: 30 } }} />
                                    <Input
                                        key={bannerInputKey}
                                        id="banner-input"
                                        type={InputTypes.file}
                                        label={translations.bannerLabel}
                                        variant={InputVariants.labeledInput}
                                        value={banner || ''}
                                        disabled={isLoadingMyAccountStoreDetail}
                                        onChange={(value) => setBanner(value)}
                                        state={errors.banner.length > 0 ? InputStates.error : undefined}
                                        errorMessages={errors.banner}
                                    />
                                </div>
                            </div>
                        )}

                        {/* logo */}
                        {block === CompanyEditBlock.logo && (
                            <div className="col-12">
                                <div className="d-flex align-items-center">
                                    {!isLoadingMyAccountStoreDetail && (
                                        <div className="position-relative">
                                            {newLogoPreview || logoPreview ? (
                                                <img
                                                    src={(newLogoPreview as string) || logoPreview || ''}
                                                    className={styles['image-preview']}
                                                    alt={translations.logoAlt}
                                                />
                                            ) : (
                                                <div className={styles['image-preview']}></div>
                                            )}
                                            <div className="position-absolute top-0 end-0 p-1">
                                                {newLogoPreview && (
                                                    <button type="button" onClick={() => setLogoInputKey(Date.now())}>
                                                        <i className="fa-solid fa-xmark" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    <Space orientation="horizontal" spaceBreakpoints={{ xs: { space: 30 } }} />
                                    <Input
                                        key={logoInputKey}
                                        id="logo-input"
                                        type={InputTypes.file}
                                        label={translations.logoLabel}
                                        variant={InputVariants.labeledInput}
                                        value={logo || ''}
                                        disabled={isLoadingMyAccountStoreDetail}
                                        onChange={(value) => setLogo(value)}
                                        state={errors.logo.length > 0 ? InputStates.error : undefined}
                                        errorMessages={errors.logo}
                                    />
                                </div>
                            </div>
                        )}

                        {block === CompanyEditBlock.nameAndSocialLinks && (
                            <>
                                {/* name */}
                                <Input
                                    type={InputTypes.text}
                                    label={`${translations.nameLabel}*`}
                                    variant={InputVariants.labeledInput}
                                    value={name}
                                    onChange={(value) => setName(value)}
                                    state={errors.name.length > 0 ? InputStates.error : undefined}
                                    errorMessages={errors.name}
                                />

                                <hr />

                                {/* caes */}
                                <div>
                                    <MultipleSelectWithSearch
                                        label={`${translations.caeLabel}*`}
                                        options={
                                            allCaes?.data?.map((cae) => ({
                                                value: cae.uuid || '',
                                                label: `${cae.cae} - ${cae.description}`,
                                            })) ?? []
                                        }
                                        value={caes.map((cae) => cae.uuid || '')}
                                        onChange={handleCAEsChange}
                                    />
                                    {errors.caes.length > 0 && <ErrorMessages messages={errors.caes} />}
                                </div>

                                {/* website */}
                                {type === 'service' && (
                                    <>
                                        <hr />
                                        <Input
                                            type={InputTypes.text}
                                            label={translations.websiteLabel}
                                            variant={InputVariants.labeledInput}
                                            value={website}
                                            onChange={(value) => setWebsite(value)}
                                            state={errors.website.length > 0 ? InputStates.error : undefined}
                                            errorMessages={errors.website}
                                        />
                                    </>
                                )}

                                <hr />

                                {isModuleActive(Modules.zones) && (
                                    <>
                                        <div>
                                            <Typography color="muted" fontSize="12">
                                                <>{translations.zoneLabel}*</>
                                            </Typography>

                                            <Select
                                                classes="w-100"
                                                type={SelectType.variant1}
                                                options={[
                                                    { text: translations.zoneRequiredError, value: '' },
                                                    ...(allZones?.data?.map((zone) => ({
                                                        text: `${zone.name}`,
                                                        value: zone.uuid || '',
                                                    })) || []),
                                                ]}
                                                value={zone}
                                                onChange={(value) => setZone(value)}
                                            />
                                            {errors.zone.length > 0 && <ErrorMessages messages={errors.zone} />}
                                        </div>
                                        <hr />
                                    </>
                                )}

                                {/* social links */}
                                {socialLinks.map((socialLink) => (
                                    <Input
                                        key={socialLink.name}
                                        type={InputTypes.text}
                                        label={socialLink.name}
                                        variant={InputVariants.labeledInput}
                                        value={socialLink.url}
                                        onChange={(value) =>
                                            setSocialLinks((previous) =>
                                                previous.map((item) => {
                                                    return {
                                                        name: item.name,
                                                        url: socialLink.name === item.name ? value : item.url,
                                                    };
                                                })
                                            )
                                        }
                                    />
                                ))}
                            </>
                        )}

                        {block === CompanyEditBlock.aboutText && (
                            <>
                                <Input
                                    type={InputTypes.textarea}
                                    label={translations.aboutTextLabel}
                                    variant={InputVariants.labeledInput}
                                    value={aboutText}
                                    onChange={(value) => setAboutText(value)}
                                />

                                {!automaticTranslations && (
                                    <TranslationTabs
                                        contentByLanguage={languagesToTranslate.map((language) => {
                                            return {
                                                language: language.language_code || '',
                                                content: (
                                                    <Input
                                                        type={InputTypes.textarea}
                                                        label={translations.aboutTextLabel}
                                                        variant={InputVariants.labeledInput}
                                                        value={
                                                            aboutTextTranslations.find(
                                                                (item) => item.languageCode === language.language_code
                                                            )?.translation || ''
                                                        }
                                                        onChange={(value) =>
                                                            setAboutTextTranslations((previous) =>
                                                                updateTranslation(previous, language.language_code || '', value)
                                                            )
                                                        }
                                                    />
                                                ),
                                            };
                                        })}
                                    />
                                )}
                            </>
                        )}

                        <Button
                            label={translations.saveButton}
                            classes="w-100"
                            mode={ButtonMode.primary}
                            onClick={handleOnClickSave}
                        />
                    </div>
                </Form>
            </div>
        </Popup>
    );
};
